import { createAction } from '@reduxjs/toolkit';
import type { IErrorType } from '../../constants/types';
import type {
    SandboxAssignmentSubmission,
    SandboxClassAsset,
    SandboxCourse,
    SandboxCourseRating,
} from '../../utils/sandboxCourseApi';
import type {
    HostOfficeHoursBooking,
    SandboxOfficeHoursConfig,
    StudentOfficeHoursBooking,
} from '../../utils/sandboxOfficeHoursApi';
import {
    types,
    type ISandboxClassAssetsResult,
    type ISandboxCourseRatingResult,
    type ISandboxExploreParams,
    type ISandboxExploreResult,
    type ISandboxPaymentHistoryResult,
} from './types';

export const fetchSandboxExplore = createAction<ISandboxExploreParams | undefined>(types.FETCH_EXPLORE);
export const fetchSandboxExploreSuccess = createAction<ISandboxExploreResult>(types.FETCH_EXPLORE_SUCCESS);
export const fetchSandboxExploreFail = createAction<IErrorType>(types.FETCH_EXPLORE_FAIL);

export const fetchSandboxCourse = createAction<string>(types.FETCH_COURSE);
export const fetchSandboxCourseSuccess = createAction<SandboxCourse>(types.FETCH_COURSE_SUCCESS);
export const fetchSandboxCourseFail = createAction<IErrorType>(types.FETCH_COURSE_FAIL);

export const fetchTeacherDashboard = createAction(types.FETCH_TEACHER_DASHBOARD);
export const fetchTeacherDashboardSuccess = createAction<any>(types.FETCH_TEACHER_DASHBOARD_SUCCESS);
export const fetchTeacherDashboardFail = createAction<IErrorType>(types.FETCH_TEACHER_DASHBOARD_FAIL);

export const createSandboxCourse = createAction<
    Partial<SandboxCourse> & { title: string; modules?: SandboxCourse['modules'] }
>(types.CREATE_COURSE);
export const createSandboxCourseSuccess = createAction<SandboxCourse>(types.CREATE_COURSE_SUCCESS);
export const createSandboxCourseFail = createAction<IErrorType>(types.CREATE_COURSE_FAIL);

export const updateSandboxCourse = createAction<{ courseId: string; body: Partial<SandboxCourse> }>(
    types.UPDATE_COURSE,
);
export const updateSandboxCourseSuccess = createAction<SandboxCourse>(types.UPDATE_COURSE_SUCCESS);
export const updateSandboxCourseFail = createAction<IErrorType>(types.UPDATE_COURSE_FAIL);

export const publishSandboxCourse = createAction<string>(types.PUBLISH_COURSE);
export const publishSandboxCourseSuccess = createAction<SandboxCourse>(types.PUBLISH_COURSE_SUCCESS);
export const publishSandboxCourseFail = createAction<IErrorType>(types.PUBLISH_COURSE_FAIL);

export const enrollSandboxFree = createAction<string>(types.ENROLL_FREE);
export const enrollSandboxFreeSuccess = createAction<any>(types.ENROLL_FREE_SUCCESS);
export const enrollSandboxFreeFail = createAction<IErrorType>(types.ENROLL_FREE_FAIL);

export const completeSandboxPurchase = createAction<{
    courseId: string;
    body: {
        saleId?: string;
        paid_cents?: number;
        currency?: string;
        subscriber_discount_applied?: boolean;
    };
}>(types.COMPLETE_PURCHASE);
export const completeSandboxPurchaseSuccess = createAction<any>(types.COMPLETE_PURCHASE_SUCCESS);
export const completeSandboxPurchaseFail = createAction<IErrorType>(types.COMPLETE_PURCHASE_FAIL);

export const saveSandboxProgress = createAction<{
    courseId: string;
    lessonId: string;
    body: { position_seconds: number; duration_seconds?: number; completed?: boolean };
}>(types.SAVE_PROGRESS);
export const saveSandboxProgressSuccess = createAction<any>(types.SAVE_PROGRESS_SUCCESS);
export const saveSandboxProgressFail = createAction<IErrorType>(types.SAVE_PROGRESS_FAIL);

export const fetchLessonProject = createAction<{ courseId: string; lessonId: string }>(types.FETCH_LESSON_PROJECT);
export const fetchLessonProjectSuccess = createAction<any>(types.FETCH_LESSON_PROJECT_SUCCESS);
export const fetchLessonProjectFail = createAction<IErrorType>(types.FETCH_LESSON_PROJECT_FAIL);

export const fetchMySandboxEnrollments = createAction(types.FETCH_MY_ENROLLMENTS);
export const fetchMySandboxEnrollmentsSuccess = createAction<any>(types.FETCH_MY_ENROLLMENTS_SUCCESS);
export const fetchMySandboxEnrollmentsFail = createAction<IErrorType>(types.FETCH_MY_ENROLLMENTS_FAIL);

export const fetchMySandboxAssignments = createAction(types.FETCH_MY_ASSIGNMENTS);
export const fetchMySandboxAssignmentsSuccess = createAction<any>(types.FETCH_MY_ASSIGNMENTS_SUCCESS);
export const fetchMySandboxAssignmentsFail = createAction<IErrorType>(types.FETCH_MY_ASSIGNMENTS_FAIL);

export const fetchTeacherPendingReviews = createAction(types.FETCH_TEACHER_REVIEWS);
export const fetchTeacherPendingReviewsSuccess = createAction<any>(types.FETCH_TEACHER_REVIEWS_SUCCESS);
export const fetchTeacherPendingReviewsFail = createAction<IErrorType>(types.FETCH_TEACHER_REVIEWS_FAIL);

export const submitSandboxAssignment = createAction<{
    courseId: string;
    lessonId: string;
    body: { project: string; notes?: string };
}>(types.SUBMIT_ASSIGNMENT);
export const submitSandboxAssignmentSuccess = createAction<any>(types.SUBMIT_ASSIGNMENT_SUCCESS);
export const submitSandboxAssignmentFail = createAction<IErrorType>(types.SUBMIT_ASSIGNMENT_FAIL);

export const fetchMySandboxLessonAssignments = createAction<{ courseId: string; lessonId: string }>(
    types.FETCH_LESSON_ASSIGNMENTS,
);
export const fetchMySandboxLessonAssignmentsSuccess = createAction<SandboxAssignmentSubmission[] | any>(
    types.FETCH_LESSON_ASSIGNMENTS_SUCCESS,
);
export const fetchMySandboxLessonAssignmentsFail = createAction<IErrorType>(types.FETCH_LESSON_ASSIGNMENTS_FAIL);

export const reviewSandboxAssignment = createAction<{
    courseId: string;
    submissionId: string;
    body: {
        status?: string;
        teacher_review?: string;
        teacher_rating?: number;
        teacher_video_note?: string;
        featured?: boolean;
    };
}>(types.REVIEW_ASSIGNMENT);
export const reviewSandboxAssignmentSuccess = createAction<any>(types.REVIEW_ASSIGNMENT_SUCCESS);
export const reviewSandboxAssignmentFail = createAction<IErrorType>(types.REVIEW_ASSIGNMENT_FAIL);

export const fetchSandboxClassAssets = createAction<string>(types.FETCH_CLASS_ASSETS);
export const fetchSandboxClassAssetsSuccess = createAction<ISandboxClassAssetsResult>(types.FETCH_CLASS_ASSETS_SUCCESS);
export const fetchSandboxClassAssetsFail = createAction<IErrorType>(types.FETCH_CLASS_ASSETS_FAIL);

export const addSandboxClassAsset = createAction<{
    courseId: string;
    body: {
        name: string;
        description?: string;
        file_key: string;
        file_size_bytes?: number;
        original_filename?: string;
    };
}>(types.ADD_CLASS_ASSET);
export const addSandboxClassAssetSuccess = createAction<SandboxClassAsset | any>(types.ADD_CLASS_ASSET_SUCCESS);
export const addSandboxClassAssetFail = createAction<IErrorType>(types.ADD_CLASS_ASSET_FAIL);

export const updateSandboxClassAsset = createAction<{
    courseId: string;
    assetId: string;
    body: { name?: string; description?: string };
}>(types.UPDATE_CLASS_ASSET);
export const updateSandboxClassAssetSuccess = createAction<SandboxClassAsset | any>(types.UPDATE_CLASS_ASSET_SUCCESS);
export const updateSandboxClassAssetFail = createAction<IErrorType>(types.UPDATE_CLASS_ASSET_FAIL);

export const deleteSandboxClassAsset = createAction<{ courseId: string; assetId: string }>(types.DELETE_CLASS_ASSET);
export const deleteSandboxClassAssetSuccess = createAction<string>(types.DELETE_CLASS_ASSET_SUCCESS);
export const deleteSandboxClassAssetFail = createAction<IErrorType>(types.DELETE_CLASS_ASSET_FAIL);

export const downloadSandboxClassAsset = createAction<{ courseId: string; assetId: string }>(
    types.DOWNLOAD_CLASS_ASSET,
);
export const downloadSandboxClassAssetSuccess = createAction<any>(types.DOWNLOAD_CLASS_ASSET_SUCCESS);
export const downloadSandboxClassAssetFail = createAction<IErrorType>(types.DOWNLOAD_CLASS_ASSET_FAIL);

export const upsertSandboxCourseRating = createAction<{
    courseId: string;
    body: { rating: number; review?: string };
}>(types.UPSERT_COURSE_RATING);
export const upsertSandboxCourseRatingSuccess = createAction<ISandboxCourseRatingResult>(
    types.UPSERT_COURSE_RATING_SUCCESS,
);
export const upsertSandboxCourseRatingFail = createAction<IErrorType>(types.UPSERT_COURSE_RATING_FAIL);

export const fetchSandboxPaymentHistory = createAction<{ page?: number; limit?: number } | undefined>(
    types.FETCH_PAYMENT_HISTORY,
);
export const fetchSandboxPaymentHistorySuccess = createAction<ISandboxPaymentHistoryResult>(
    types.FETCH_PAYMENT_HISTORY_SUCCESS,
);
export const fetchSandboxPaymentHistoryFail = createAction<IErrorType>(types.FETCH_PAYMENT_HISTORY_FAIL);

export const fetchMyOfficeHours = createAction(types.FETCH_MY_OFFICE_HOURS);
export const fetchMyOfficeHoursSuccess = createAction<SandboxOfficeHoursConfig | any>(
    types.FETCH_MY_OFFICE_HOURS_SUCCESS,
);
export const fetchMyOfficeHoursFail = createAction<IErrorType>(types.FETCH_MY_OFFICE_HOURS_FAIL);

export const upsertMyOfficeHours = createAction<Partial<SandboxOfficeHoursConfig>>(types.UPSERT_MY_OFFICE_HOURS);
export const upsertMyOfficeHoursSuccess = createAction<SandboxOfficeHoursConfig | any>(
    types.UPSERT_MY_OFFICE_HOURS_SUCCESS,
);
export const upsertMyOfficeHoursFail = createAction<IErrorType>(types.UPSERT_MY_OFFICE_HOURS_FAIL);

export const fetchOfficeHoursGoogleAuthorizeUrl = createAction(types.FETCH_GOOGLE_AUTHORIZE_URL);
export const fetchOfficeHoursGoogleAuthorizeUrlSuccess = createAction<any>(types.FETCH_GOOGLE_AUTHORIZE_URL_SUCCESS);
export const fetchOfficeHoursGoogleAuthorizeUrlFail = createAction<IErrorType>(types.FETCH_GOOGLE_AUTHORIZE_URL_FAIL);

export const fetchTeacherOfficeHours = createAction<string>(types.FETCH_TEACHER_OFFICE_HOURS);
export const fetchTeacherOfficeHoursSuccess = createAction<SandboxOfficeHoursConfig | any>(
    types.FETCH_TEACHER_OFFICE_HOURS_SUCCESS,
);
export const fetchTeacherOfficeHoursFail = createAction<IErrorType>(types.FETCH_TEACHER_OFFICE_HOURS_FAIL);

export const fetchTeacherOfficeHoursAvailability = createAction<{
    teacherId: string;
    date: string;
    timezone?: string;
}>(types.FETCH_TEACHER_OH_AVAILABILITY);
export const fetchTeacherOfficeHoursAvailabilitySuccess = createAction<any>(
    types.FETCH_TEACHER_OH_AVAILABILITY_SUCCESS,
);
export const fetchTeacherOfficeHoursAvailabilityFail = createAction<IErrorType>(
    types.FETCH_TEACHER_OH_AVAILABILITY_FAIL,
);

export const bookTeacherOfficeHours = createAction<{
    teacherId: string;
    body: { start_time: string; notes?: string; timezone?: string; project_id?: string };
}>(types.BOOK_TEACHER_OFFICE_HOURS);
export const bookTeacherOfficeHoursSuccess = createAction<any>(types.BOOK_TEACHER_OFFICE_HOURS_SUCCESS);
export const bookTeacherOfficeHoursFail = createAction<IErrorType>(types.BOOK_TEACHER_OFFICE_HOURS_FAIL);

export const fetchMyHostOfficeHourBookings = createAction(types.FETCH_HOST_BOOKINGS);
export const fetchMyHostOfficeHourBookingsSuccess = createAction<HostOfficeHoursBooking[] | any>(
    types.FETCH_HOST_BOOKINGS_SUCCESS,
);
export const fetchMyHostOfficeHourBookingsFail = createAction<IErrorType>(types.FETCH_HOST_BOOKINGS_FAIL);

export const cancelMyHostOfficeHourBooking = createAction<{ bookingId: string; reason: string }>(
    types.CANCEL_HOST_BOOKING,
);
export const cancelMyHostOfficeHourBookingSuccess = createAction<any>(types.CANCEL_HOST_BOOKING_SUCCESS);
export const cancelMyHostOfficeHourBookingFail = createAction<IErrorType>(types.CANCEL_HOST_BOOKING_FAIL);

export const fetchMyStudentOfficeHourBookings = createAction(types.FETCH_STUDENT_BOOKINGS);
export const fetchMyStudentOfficeHourBookingsSuccess = createAction<StudentOfficeHoursBooking[] | any>(
    types.FETCH_STUDENT_BOOKINGS_SUCCESS,
);
export const fetchMyStudentOfficeHourBookingsFail = createAction<IErrorType>(types.FETCH_STUDENT_BOOKINGS_FAIL);

export const cancelMyStudentOfficeHourBooking = createAction<{ bookingId: string; reason?: string }>(
    types.CANCEL_STUDENT_BOOKING,
);
export const cancelMyStudentOfficeHourBookingSuccess = createAction<any>(types.CANCEL_STUDENT_BOOKING_SUCCESS);
export const cancelMyStudentOfficeHourBookingFail = createAction<IErrorType>(types.CANCEL_STUDENT_BOOKING_FAIL);

export const rescheduleMyStudentOfficeHourBooking = createAction<{
    bookingId: string;
    body: { start_time: string; date?: string; timezone?: string };
}>(types.RESCHEDULE_STUDENT_BOOKING);
export const rescheduleMyStudentOfficeHourBookingSuccess = createAction<any>(types.RESCHEDULE_STUDENT_BOOKING_SUCCESS);
export const rescheduleMyStudentOfficeHourBookingFail = createAction<IErrorType>(types.RESCHEDULE_STUDENT_BOOKING_FAIL);

export const requestMyStudentOfficeHourRefund = createAction<{ bookingId: string; reason?: string }>(
    types.REQUEST_STUDENT_OH_REFUND,
);
export const requestMyStudentOfficeHourRefundSuccess = createAction<any>(types.REQUEST_STUDENT_OH_REFUND_SUCCESS);
export const requestMyStudentOfficeHourRefundFail = createAction<IErrorType>(types.REQUEST_STUDENT_OH_REFUND_FAIL);

export const uploadSandboxCoverImage = createAction<{ file: File; courseId: string }>(types.UPLOAD_COVER_IMAGE);
export const uploadSandboxCoverImageSuccess = createAction<string>(types.UPLOAD_COVER_IMAGE_SUCCESS);
export const uploadSandboxCoverImageFail = createAction<IErrorType>(types.UPLOAD_COVER_IMAGE_FAIL);

export const uploadSandboxClassAssetZip = createAction<{ file: File; courseId: string }>(types.UPLOAD_CLASS_ASSET_ZIP);
export const uploadSandboxClassAssetZipSuccess = createAction<string>(types.UPLOAD_CLASS_ASSET_ZIP_SUCCESS);
export const uploadSandboxClassAssetZipFail = createAction<IErrorType>(types.UPLOAD_CLASS_ASSET_ZIP_FAIL);

// Re-export rating type for consumers that previously imported from api util actions path
export type { SandboxCourseRating };
